import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const getSessionId = (): string => {
  let sid = sessionStorage.getItem('_ss_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('_ss_sid', sid);
  }
  return sid;
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const pageEntryTime = useRef(Date.now());
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const sessionId = getSessionId();
    const now = Date.now();

    // Track duration of previous page
    if (lastPath.current && lastPath.current !== location.pathname) {
      const duration = Math.round((now - pageEntryTime.current) / 1000);
      if (duration > 0 && duration < 3600) {
        (supabase as any).from('site_analytics_events').insert({
          session_id: sessionId,
          user_id: user?.id || null,
          event_type: 'page_duration',
          page_path: lastPath.current,
          duration_seconds: duration,
        }).then(() => {});
      }
    }

    pageEntryTime.current = now;
    lastPath.current = location.pathname;

    // Track page view
    (supabase as any).from('site_analytics_events').insert({
      session_id: sessionId,
      user_id: user?.id || null,
      event_type: 'page_view',
      page_path: location.pathname,
      page_title: document.title,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      metadata: { search: location.search, hash: location.hash },
    }).then(() => {});

    // Track session start
    if (!lastPath.current || lastPath.current === location.pathname) {
      (supabase as any).from('site_analytics_events').insert({
        session_id: sessionId,
        user_id: user?.id || null,
        event_type: 'session_start',
        page_path: location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
      }).then(() => {});
    }
  }, [location.pathname, user?.id]);
};
